import { useApolloClient } from '@apollo/client/react/hooks';
import { revertConflictingFields } from '@oolio-group/client-utils';
import {
  Order,
  OrderAction,
  OrderEvent,
  OrderStatus,
} from '@oolio-group/domain';
import { encodeHlc, getHlc } from '@oolio-group/order-helper';
import { orderEventsHandler } from '../../utils/orderEventsHandler';
import { useCallback, useEffect, useRef, useState } from 'react';
import { observeOn, queueScheduler } from 'rxjs';
import { sessionSubject } from '../../state/sessionObservable';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { cdsOrderEventsSubject } from './cdsOrderEventsObservable';
import {
  BaseEventEmitter,
  subscribeOrderEventsStream,
} from '../../utils/localComms';
import { CDS_ORDER_EVENT } from '../../graphql/subscription';
import { localCommsSubject } from '../../state/localComms';
import { upsellComboStore } from '../../store/upsellCombo';
import Sentry from '../../utils/Sentry/Sentry';

const useCartEvent = () => {
  const [order, setOrder] = useState<Order>({} as Order);
  // used to calculate new order data based. changing preOrder won't make component rerender
  const preOrder = useRef<Order>({} as Order);
  const orderEvents = useRef<OrderEvent[]>([]);
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const client = useApolloClient();
  const { value: isLocalCommsEligible } =
    useBehaviorSubjectState(localCommsSubject);

  const onOrderEventsReceived = useCallback(async (events: OrderEvent[]) => {
    if (!events.length) return;
    // Advance the local HLC past any incoming remote observations so
    // subsequent local events are ordered strictly after what we just saw.
    const maxHlc = events.reduce<number>(
      (acc, e) => Math.max(acc, e.hlc ?? encodeHlc(e.timestamp, 0)),
      0,
    );
    if (maxHlc > 0) {
      getHlc().receive(maxHlc);
    }
    let eventArray = revertConflictingFields(events);
    const isCloseOrder = eventArray.some(
      event => event.action === OrderAction.ORDER_CLOSE,
    );
    if (isCloseOrder) {
      preOrder.current = {} as Order;
      setOrder({} as Order);
      upsellComboStore.setState(eventArray);
      orderEvents.current = [];
      return;
    }

    const isSameOrder = eventArray.every(
      event => event.orderId === preOrder.current.id,
    );

    // A lone ORDER_INITIATE for a *different* order (e.g. another table being
    // started on the POS) must not be merged into the current order's event
    // stream. Mixing order ids makes orderUpdateEventsValidation reject the
    // whole accumulated batch, which permanently wipes the reduced order (and
    // with it the ability to auto-launch a re-attempted payment on a recalled
    // order). Ignore it while an order is already on screen.
    const isForeignLoneInitiate =
      !isSameOrder &&
      !!preOrder.current.id &&
      eventArray.length === 1 &&
      eventArray[0].action === OrderAction.ORDER_INITIATE;
    if (isForeignLoneInitiate) {
      return;
    }

    if (
      !isSameOrder &&
      !(
        eventArray.length === 1 &&
        eventArray[0].action === OrderAction.ORDER_INITIATE
      )
    ) {
      const lastOrderId = eventArray[eventArray.length - 1].orderId;
      eventArray = eventArray.filter(event => event.orderId === lastOrderId);
      orderEvents.current = [];
    }

    orderEvents.current = orderEvents.current.concat(eventArray);
    const newOrder = await orderEventsHandler(orderEvents.current, {} as Order);
    if (newOrder.id && newOrder.status !== OrderStatus.VOID) {
      setOrder(newOrder);
      upsellComboStore.setState(eventArray);
      preOrder.current = newOrder;
    }
  }, []);

  useEffect(() => {
    const sub = cdsOrderEventsSubject
      .pipe(observeOn(queueScheduler))
      .subscribe(async (events: OrderEvent[]) => {
        await onOrderEventsReceived(events);
      });

    return () => {
      sub.unsubscribe();
    };
  }, [onOrderEventsReceived]);
  useEffect(() => {
    let emitter: InstanceType<typeof BaseEventEmitter> | null = null;
    let subscription: { unsubscribe?: () => void } | null = null;

    if (isLocalCommsEligible === true) {
      // Local comms is the active source
      const subscribeToEvents = async () => {
        const topic = `orderEvents.store.${session?.currentStore?.id}.device.${session?.device?.id}`;
        emitter = await subscribeOrderEventsStream(topic);
        emitter?.addListener((eventsData: unknown) => {
          try {
            const payloadRaw: unknown =
              typeof eventsData === 'string'
                ? JSON.parse(eventsData)
                : eventsData;

            const payload = payloadRaw as { events?: string } | undefined;
            if (!payload?.events) return;

            const newEvents = JSON.parse(payload.events) as Array<OrderEvent>;
            cdsOrderEventsSubject.next(newEvents);
          } catch {
            // ignore malformed events
          }
        });
      };
      subscribeToEvents();
    } else {
      // Web subscription is the active source
      const observable = client.subscribe({
        query: CDS_ORDER_EVENT,
        variables: {
          cdsId: session.customerDisplay?.id,
          deviceId: session?.device?.id,
          lastActiveTime: 0,
          orderId: order.id,
        },
      });

      subscription = observable.subscribe({
        next({ data }) {
          if (data?.cdsOrderEvents) {
            cdsOrderEventsSubject.next(data?.cdsOrderEvents);
          }
        },
        error(err) {
          Sentry.captureException(err, {
            extra: {
              context: 'cds-order-event-subscription',
              orderId: order.id,
            },
          });
        },
        complete() {
          Sentry.captureException(
            new Error('CDS order-event subscription closed'),
            {
              extra: {
                context: 'cds-order-event-subscription',
                orderId: order.id,
              },
            },
          );
        },
      });
    }

    return () => {
      if (emitter instanceof BaseEventEmitter) {
        emitter.remove();
      }
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [
    client,
    isLocalCommsEligible,
    order.id,
    session?.currentStore?.id,
    session.customerDisplay?.id,
    session?.device?.id,
  ]);
  return {
    order,
  };
};

export default useCartEvent;
